import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, getUserFacingErrorMessage } from './services/apiClient';
import './SupportPages.css';

function formatStatus(status) {
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function SupportTicketDetailPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentAck, setSentAck] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/support-tickets/${encodeURIComponent(ticketId)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          getUserFacingErrorMessage(res.status, data, data.detail || 'Failed to load ticket')
        );
      }
      setTicket(data.ticket || null);
    } catch (err) {
      setError(err.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending || !ticket) return;
    setSending(true);
    setError(null);
    setSentAck(null);
    try {
      const ref = ticket.public_id || ticket.id;
      const res = await apiFetch(`/api/support-tickets/${encodeURIComponent(ref)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: reply.trim(), visibility: 'customer' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          getUserFacingErrorMessage(res.status, data, data.detail || 'Failed to send reply')
        );
      }
      setTicket(data.ticket || ticket);
      setReply('');
      setSentAck('Reply sent');
      setEditingId(null);
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditBody(m.body || '');
    setSentAck(null);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!ticket || !editingId || !editBody.trim() || editBusy) return;
    setEditBusy(true);
    setError(null);
    try {
      const ref = ticket.public_id || ticket.id;
      const res = await apiFetch(
        `/api/support-tickets/${encodeURIComponent(ref)}/messages/${editingId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: editBody.trim() }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          getUserFacingErrorMessage(res.status, data, data.detail || 'Failed to save edit')
        );
      }
      setTicket(data.ticket || ticket);
      setEditingId(null);
      setEditBody('');
      setSentAck('Message updated');
    } catch (err) {
      setError(err.message || 'Failed to save edit');
    } finally {
      setEditBusy(false);
    }
  };

  const closed = ticket?.status === 'closed';

  return (
    <div className="support-page">
      <div className="support-header">
        <div>
          <p className="support-eyebrow">Help &amp; Support</p>
          <h1>{ticket?.public_id || 'Ticket'}</h1>
        </div>
        <Link to="/support" className="support-btn secondary">
          All tickets
        </Link>
      </div>

      <div className="glass-container support-card">
        {error && <div className="support-error">{error}</div>}
        {loading ? (
          <p className="support-empty">Loading…</p>
        ) : !ticket ? (
          <p className="support-empty">Ticket not found.</p>
        ) : (
          <>
            <h2 style={{ marginTop: 0 }}>{ticket.subject}</h2>
            <div className="support-detail-meta">
              <span className={`support-badge status-${ticket.status}`}>
                {formatStatus(ticket.status)}
              </span>
              <span className="support-badge">{ticket.category}</span>
              <span className="support-badge">{ticket.priority}</span>
            </div>

            <div className="support-thread">
              {(ticket.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`support-msg ${m.visibility === 'internal' ? 'internal' : ''}`}
                >
                  <div className="support-msg-meta">
                    <span>
                      {m.visibility === 'internal'
                        ? 'Internal note'
                        : m.author_label || 'Message'}
                    </span>
                    <span>
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  {editingId === m.id ? (
                    <form className="support-edit-form" onSubmit={saveEdit}>
                      <textarea
                        rows={4}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        disabled={editBusy}
                        required
                      />
                      <div className="support-edit-actions">
                        <button
                          type="button"
                          className="support-btn secondary"
                          onClick={cancelEdit}
                          disabled={editBusy}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="support-btn"
                          disabled={editBusy || !editBody.trim()}
                        >
                          {editBusy ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <p className="support-msg-body">{m.body}</p>
                      {m.can_edit && (
                        <button
                          type="button"
                          className="support-edit-link"
                          onClick={() => startEdit(m)}
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {sentAck && <p className="support-success">{sentAck}</p>}

            {!closed && (
              <form className="support-form" onSubmit={sendReply}>
                <label>
                  <span>Your reply</span>
                  <textarea
                    rows={4}
                    value={reply}
                    onChange={(e) => {
                      setReply(e.target.value);
                      if (sentAck) setSentAck(null);
                    }}
                    disabled={sending}
                    placeholder="Add more details for the support team…"
                    required
                  />
                </label>
                <p className="support-hint">
                  You can edit your last message for 30 minutes if support has not replied yet.
                </p>
                <button type="submit" className="support-btn" disabled={sending || !reply.trim()}>
                  {sending ? 'Sending…' : 'Send reply'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SupportTicketDetailPage;
